import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Circle as CircleIcon, Tag, Download, Trash2, CheckSquare, Square } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCircles } from '@/hooks/use-circles'
import { useLabels } from '@/hooks/use-labels'
import { useBulkAddToCircle, useBulkAddLabel, useBulkDeleteContacts } from '@/hooks/use-contacts'
import type { Circle, Label } from '@/lib/types'

interface BatchActionsToolbarProps {
  selectedIds: Set<string>
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onDeselectAll: () => void
  onExit: () => void
}

export default function BatchActionsToolbar({
  selectedIds,
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onExit,
}: BatchActionsToolbarProps) {
  const { t } = useTranslation()
  const [showCirclePicker, setShowCirclePicker] = useState(false)
  const [showLabelPicker, setShowLabelPicker] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: circlesData } = useCircles()
  const circles = circlesData ?? []
  const { data: labelsData } = useLabels()
  const labels = labelsData ?? []

  const bulkAddToCircle = useBulkAddToCircle()
  const bulkAddLabel = useBulkAddLabel()
  const bulkDelete = useBulkDeleteContacts()

  const ids = Array.from(selectedIds)
  const allSelected = selectedCount === totalCount && totalCount > 0

  function handleAddToCircle(circle: Circle) {
    bulkAddToCircle.mutate(
      { contact_ids: ids, circle_id: circle.id },
      { onSuccess: () => setShowCirclePicker(false) },
    )
  }

  function handleAddLabel(label: Label) {
    bulkAddLabel.mutate(
      { contact_ids: ids, label_id: label.id },
      { onSuccess: () => setShowLabelPicker(false) },
    )
  }

  function handleExport() {
    const url = `/api/contacts/export?ids=${ids.join(',')}`
    window.open(url, '_blank')
  }

  function handleDelete() {
    bulkDelete.mutate(ids, {
      onSuccess: () => {
        setShowDeleteConfirm(false)
        onExit()
      },
    })
  }

  return (
    <>
      {/* Toolbar */}
      <div className="bg-primary/10 border-primary/20 flex items-center gap-2 border-b px-4 py-2">
        {/* Select all toggle */}
        <button
          onClick={allSelected ? onDeselectAll : onSelectAll}
          className="text-primary flex items-center gap-1.5 text-sm font-medium"
        >
          {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
          {allSelected ? t('batch.deselectAll') : t('batch.selectAll')}
        </button>

        <span className="text-muted-foreground text-xs">
          {t('batch.selected', { count: selectedCount })}
        </span>

        <div className="flex-1" />

        {/* Action buttons */}
        <button
          onClick={() => setShowCirclePicker(true)}
          disabled={selectedCount === 0}
          className="text-foreground rounded-lg p-2 transition-colors hover:bg-black/5 disabled:opacity-40"
          title={t('batch.addToCircle')}
        >
          <CircleIcon className="h-4.5 w-4.5" />
        </button>
        <button
          onClick={() => setShowLabelPicker(true)}
          disabled={selectedCount === 0}
          className="text-foreground rounded-lg p-2 transition-colors hover:bg-black/5 disabled:opacity-40"
          title={t('batch.applyLabel')}
        >
          <Tag className="h-4.5 w-4.5" />
        </button>
        <button
          onClick={handleExport}
          disabled={selectedCount === 0}
          className="text-foreground rounded-lg p-2 transition-colors hover:bg-black/5 disabled:opacity-40"
          title={t('batch.export')}
        >
          <Download className="h-4.5 w-4.5" />
        </button>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={selectedCount === 0}
          className="text-destructive hover:bg-destructive/10 rounded-lg p-2 transition-colors disabled:opacity-40"
          title={t('batch.deleteSelected')}
        >
          <Trash2 className="h-4.5 w-4.5" />
        </button>

        {/* Close selection mode */}
        <button
          onClick={onExit}
          className="text-muted-foreground rounded-lg p-2 transition-colors hover:bg-black/5"
        >
          <X className="h-4.5 w-4.5" />
        </button>
      </div>

      {/* Circle picker */}
      {showCirclePicker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="bg-surface-container-low w-full max-w-sm rounded-t-xl p-6 shadow-lg sm:rounded-xl">
            <h2 className="mb-4 text-lg font-semibold">{t('batch.addToCircle')}</h2>
            {circles.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('circles.empty')}</p>
            ) : (
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {circles.map((circle) => (
                  <button
                    key={circle.id}
                    onClick={() => handleAddToCircle(circle)}
                    disabled={bulkAddToCircle.isPending}
                    className="hover:bg-primary/5 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors"
                  >
                    {circle.color && (
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: circle.color }}
                      />
                    )}
                    <span className="text-sm font-medium">{circle.name}</span>
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowCirclePicker(false)}
              className="text-muted-foreground mt-4 w-full rounded-lg py-2 text-sm hover:bg-black/5"
            >
              {t('contacts.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Label picker */}
      {showLabelPicker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="bg-surface-container-low w-full max-w-sm rounded-t-xl p-6 shadow-lg sm:rounded-xl">
            <h2 className="mb-4 text-lg font-semibold">{t('batch.applyLabel')}</h2>
            {labels.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('labels.empty')}</p>
            ) : (
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {(() => {
                  const rootLabels = labels.filter((l) => !l.parentId)
                  const childMap = new Map<string, typeof labels>()
                  for (const l of labels) {
                    if (l.parentId) {
                      const existing = childMap.get(l.parentId) ?? []
                      existing.push(l)
                      childMap.set(l.parentId, existing)
                    }
                  }

                  const renderLabelButton = (label: Label, indent: boolean) => (
                    <button
                      key={label.id}
                      onClick={() => handleAddLabel(label)}
                      disabled={bulkAddLabel.isPending}
                      className={cn(
                        'hover:bg-primary/5 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        indent && 'pl-8',
                      )}
                    >
                      {label.color && (
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: label.color }}
                        />
                      )}
                      <span className="text-sm font-medium">{label.name}</span>
                    </button>
                  )

                  return rootLabels.flatMap((parent) => [
                    renderLabelButton(parent, false),
                    ...(childMap.get(parent.id) ?? []).map((child) =>
                      renderLabelButton(child, true),
                    ),
                  ])
                })()}
              </div>
            )}
            <button
              onClick={() => setShowLabelPicker(false)}
              className="text-muted-foreground mt-4 w-full rounded-lg py-2 text-sm hover:bg-black/5"
            >
              {t('contacts.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface-container-low w-full max-w-sm rounded-xl p-6 shadow-lg">
            <h2 className="text-lg font-semibold">{t('batch.deleteSelected')}</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {t('batch.deleteConfirm', { count: selectedCount })}
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={cn(
                  'border-outline-variant/30 flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium',
                  'hover:bg-surface-container-high transition-colors',
                )}
              >
                {t('contacts.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={bulkDelete.isPending}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {bulkDelete.isPending ? t('batch.deleting') : t('contacts.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
